import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Power, PowerOff, Trash2, Search, RefreshCw, ArrowLeft, Eraser, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowUpDown, Pause, Play, Eye, Activity, Copy, ExternalLink, HelpCircle, X, Globe, Wifi, ShieldCheck, ArrowRight } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTenantList, useCreateTenant, useUpdateTenant, useActivateTenant, useDeactivateTenant, useDestroyTenant, usePauseProbe, useResumeProbe, useCleanTenantData } from '../hooks/useTenants.ts';

import { ConfirmModal } from '@/shared/components/ConfirmModal.tsx';
import { RowActionMenu, type RowAction } from '@/shared/components/RowActionMenu.tsx';
import { InfoTooltip, TooltipHeader, TooltipBody, TooltipDetail, TooltipTip } from '@/shared/components/InfoTooltip.tsx';
import { TenantDetailModal } from './TenantDetailModal.tsx';
import type { TenantRow, TenantListParams } from '@/shared/types/tenant.ts';

type StatusFilter = 'all' | 'active' | 'inactive';
type ViewMode = 'list' | 'form';

const columnHelper = createColumnHelper<TenantRow>();

const faixaFieldSchema = z.object({
  uptimeMin: z.string(),
  descontoPct: z.string(),
});

// Campos de contrato no form são strings (convertidos no submit); todos opcionais.
const contratoFormFields = {
  contratoInicioEm: z.string().optional(),
  slaAlvo: z.string().optional(),
  slaPeriodicidade: z.string().optional(),
  faixasDesconto: z.array(faixaFieldSchema).optional(),
};

// Host puro do domínio público da página de status (opcional; vazio = resolve pelo slug).
const dominioStatusFormField = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
    'Informe apenas o host, ex.: cliente.wolihub.com.br (sem https:// e sem barra)',
  )
  .or(z.literal(''))
  .optional();

const createSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  slug: z
    .string()
    .min(2, 'Slug deve ter no mínimo 2 caracteres')
    .max(50, 'Slug deve ter no máximo 50 caracteres')
    .regex(/^[a-z0-9-]+$/, 'Apenas letras minúsculas, números e hífens'),
  urlProbe: z.string().url('URL inválida'),
  poolIIS: z.string().optional().or(z.literal('')),
  dominioStatus: dominioStatusFormField,
  ...contratoFormFields,
});

const updateSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  slug: z.string(),
  urlProbe: z.string().url('URL inválida'),
  poolIIS: z.string().optional().or(z.literal('')),
  dominioStatus: dominioStatusFormField,
  ...contratoFormFields,
});

type FormData = z.infer<typeof createSchema>;

function StatusBadge({ isActive }: { isActive: boolean }) {
  if (isActive) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
        style={{ background: 'var(--color-status-up)' }}
      >
        Ativo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-300 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-600 dark:text-gray-300">
      Inativo
    </span>
  );
}

function ProbeBadge({ probePaused }: { probePaused: boolean }) {
  if (!probePaused) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
      Pausado
    </span>
  );
}

// ── Modal: Como funcionam os tenants ──────────────────────
function TenantHelpModal({ onClose }: { onClose: () => void }) {
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative mx-4 w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              <HelpCircle size={18} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Como funciona o monitoramento?</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* O que é um Tenant */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Globe size={16} className="text-blue-600 dark:text-blue-400" />
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">O que é um Tenant?</h4>
            </div>
            <div className="space-y-1.5 text-xs text-blue-700 dark:text-blue-300/90">
              <p>Cada <strong>tenant</strong> representa um cliente Woli monitorado. Todo tenant possui um subdomínio exclusivo: <strong>slug.woli.com.br</strong></p>
              <p>Exemplo: o tenant com slug <code className="rounded bg-blue-100 px-1 py-0.5 dark:bg-blue-900/40">acme</code> tem sua página pública em <strong>acme.woli.com.br/uptime</strong></p>
            </div>
          </div>

          {/* URL de Probe */}
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Wifi size={16} className="text-green-600 dark:text-green-400" />
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">URL de Probe</h4>
            </div>
            <div className="space-y-1.5 text-xs text-green-700 dark:text-green-300/90">
              <p>É o endereço que o worker verifica <strong>a cada 1 minuto</strong> para checar disponibilidade.</p>
              <p>Normalmente aponta para <code className="rounded bg-green-100 px-1 py-0.5 dark:bg-green-900/40">slug.woli.com.br/health</code></p>
              <div className="mt-2 space-y-1">
                <p><strong className="text-green-700 dark:text-green-300">UP:</strong> qualquer resposta HTTP (mesmo 4xx/5xx = servidor respondeu)</p>
                <p><strong className="text-red-600 dark:text-red-400">DOWN:</strong> timeout, connection refused, falha DNS, erro de rede</p>
              </div>
              <p className="mt-1 text-[11px] italic text-green-600 dark:text-green-400/70">Cada probe faz retry automático com timeout estendido (30s) antes de confirmar DOWN.</p>
            </div>
          </div>

          {/* Anti-flap e SLA */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800/50 dark:bg-orange-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <ShieldCheck size={16} className="text-orange-600 dark:text-orange-400" />
              <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">Anti-Flap e SLA</h4>
            </div>
            <div className="space-y-2 text-xs text-orange-700 dark:text-orange-300/90">
              <p>Para evitar falsos positivos, são necessários <strong>3 checks DOWN consecutivos</strong> para declarar indisponibilidade:</p>
              <div className="flex items-center gap-1.5 rounded-md bg-orange-100 px-3 py-2 font-mono text-[11px] dark:bg-orange-900/30">
                <span className="rounded bg-red-200 px-1.5 py-0.5 text-red-700 dark:bg-red-900/50 dark:text-red-300">DOWN</span>
                <ArrowRight size={12} />
                <span className="rounded bg-red-200 px-1.5 py-0.5 text-red-700 dark:bg-red-900/50 dark:text-red-300">DOWN</span>
                <ArrowRight size={12} />
                <span className="rounded bg-red-200 px-1.5 py-0.5 text-red-700 dark:bg-red-900/50 dark:text-red-300">DOWN</span>
                <ArrowRight size={12} />
                <span className="rounded bg-red-600 px-1.5 py-0.5 text-white">INCIDENTE</span>
              </div>
              <p><strong>SLA contratual:</strong> 99% de uptime em 30 dias corridos. Manutenções cadastradas são excluídas do cálculo.</p>
            </div>
          </div>

          {/* Ações disponíveis */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Ações disponíveis</h4>
            <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
              <p><strong>Pausar Probe:</strong> suspende temporariamente a verificação do tenant (ex: manutenção planejada)</p>
              <p><strong>Limpar Dados:</strong> remove probes, incidentes e summaries do tenant (útil para recomeçar)</p>
              <p><strong>Monitoramento:</strong> abre a página de status detalhada do tenant (mesma visão da página pública)</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Filtro inicial da tela e do botao Limpar.
 * 'active' porque a base tem centenas de tenants inativos: abrir em "Todos" enterra
 * os que estao de fato sendo monitorados.
 */
const STATUS_FILTER_PADRAO: StatusFilter = 'active';

export function TenantListPage() {
  const navigate = useNavigate();

  // ── Estado local ────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(STATUS_FILTER_PADRAO);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'nome', desc: false }]);
  const [searched, setSearched] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingTenant, setEditingTenant] = useState<TenantRow | null>(null);

  const [confirmDeactivate, setConfirmDeactivate] = useState<TenantRow | null>(null);
  const [confirmDestroy, setConfirmDestroy] = useState<TenantRow | null>(null);
  const [confirmCleanData, setConfirmCleanData] = useState<TenantRow | null>(null);
  // Corte "apagar tudo antes de" (datetime-local); vazio = apagar todo o histórico.
  const [cleanBefore, setCleanBefore] = useState('');
  const [detailTenantId, setDetailTenantId] = useState<string | null>(null);

  const isEditing = editingTenant !== null;

  // ── Form ──────────────────────────────────────────────────
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(isEditing ? updateSchema : createSchema),
    defaultValues: {
      nome: '', slug: '', urlProbe: '', poolIIS: '', dominioStatus: '',
      contratoInicioEm: '', slaAlvo: '', slaPeriodicidade: '', faixasDesconto: [],
    },
  });

  const { fields: faixaFields, append: appendFaixa, remove: removeFaixa } = useFieldArray({
    control,
    name: 'faixasDesconto',
  });

  useEffect(() => {
    if (editingTenant) {
      reset({
        nome: editingTenant.nome,
        slug: editingTenant.slug,
        urlProbe: editingTenant.urlProbe,
        poolIIS: editingTenant.poolIIS ?? '',
        dominioStatus: editingTenant.dominioStatus ?? '',
        contratoInicioEm: editingTenant.contratoInicioEm ? editingTenant.contratoInicioEm.slice(0, 10) : '',
        slaAlvo: editingTenant.slaAlvo != null ? String(Number(editingTenant.slaAlvo)) : '',
        slaPeriodicidade: editingTenant.slaPeriodicidade ?? '',
        faixasDesconto: (editingTenant.faixasDesconto ?? []).map((f) => ({
          uptimeMin: String(f.uptimeMin),
          descontoPct: String(f.descontoPct),
        })),
      });
    } else {
      reset({
        nome: '', slug: '', urlProbe: '', poolIIS: '', dominioStatus: '',
        contratoInicioEm: '', slaAlvo: '', slaPeriodicidade: '', faixasDesconto: [],
      });
    }
  }, [editingTenant, reset]);

  // ── Queries / mutations ─────────────────────────────────────
  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: search || undefined,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      sortBy: (sorting[0]?.id as TenantListParams['sortBy']) || 'nome',
      sortOrder: (sorting[0]?.desc ? 'desc' : 'asc') as TenantListParams['sortOrder'],
    }),
    [pagination.pageIndex, pagination.pageSize, search, statusFilter, sorting],
  );

  const { data: response, isLoading } = useTenantList(params, searched);

  const createMutation = useCreateTenant(() => { setViewMode('list'); });
  const updateMutation = useUpdateTenant(() => { setViewMode('list'); setEditingTenant(null); });
  const activateMutation = useActivateTenant();
  const deactivateMutation = useDeactivateTenant(() => setConfirmDeactivate(null));
  const destroyMutation = useDestroyTenant(() => setConfirmDestroy(null));
  const pauseProbeMutation = usePauseProbe();
  const resumeProbeMutation = useResumeProbe();
  const cleanDataMutation = useCleanTenantData(() => setConfirmCleanData(null));

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  function onSubmit(data: FormData) {
    // Converte os campos de contrato (strings do form) para o payload da API; vazio = null.
    const faixas = (data.faixasDesconto ?? [])
      .filter((f) => f.uptimeMin !== '' && f.descontoPct !== '')
      .map((f) => ({ uptimeMin: Number(f.uptimeMin), descontoPct: Number(f.descontoPct) }))
      .filter((f) => Number.isFinite(f.uptimeMin) && Number.isFinite(f.descontoPct));

    const contrato = {
      contratoInicioEm: data.contratoInicioEm ? new Date(data.contratoInicioEm).toISOString() : null,
      slaAlvo: data.slaAlvo ? Number(data.slaAlvo) : null,
      slaPeriodicidade: (data.slaPeriodicidade || null) as 'MENSAL' | 'ANUAL' | null,
      faixasDesconto: faixas.length > 0 ? faixas : null,
    };

    const dominioStatus = data.dominioStatus || null;

    if (isEditing && editingTenant) {
      updateMutation.mutate({
        id: editingTenant.id,
        input: {
          nome: data.nome, urlProbe: data.urlProbe, poolIIS: data.poolIIS || null,
          dominioStatus, ...contrato,
        },
      });
    } else {
      createMutation.mutate({
        nome: data.nome, slug: data.slug, urlProbe: data.urlProbe, poolIIS: data.poolIIS || null,
        dominioStatus, ...contrato,
      });
    }
  }

  function handleSearch() {
    setSearched(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  function handleClear() {
    setSearch('');
    setStatusFilter(STATUS_FILTER_PADRAO);
    setSorting([{ id: 'nome', desc: false }]);
    setSearched(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  // ── Colunas ─────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'actions',
        header: 'Ações',
        enableSorting: false,
        cell: ({ row }) => {
          const t = row.original;
          const actions: RowAction[] = [
            {
              label: 'Ver Detalhes',
              icon: <Eye size={15} />,
              onClick: () => setDetailTenantId(t.id),
            },
            {
              label: 'Monitoramento',
              icon: <Activity size={15} />,
              onClick: () => navigate(`/admin/tenants/${t.id}/status`),
            },
            {
              label: 'Editar',
              icon: <Pencil size={15} />,
              onClick: () => { setEditingTenant(t); setViewMode('form'); },
            },
            t.isActive
              ? {
                  label: 'Desativar',
                  icon: <PowerOff size={15} />,
                  onClick: () => setConfirmDeactivate(t),
                }
              : {
                  label: 'Ativar',
                  icon: <Power size={15} />,
                  onClick: () => activateMutation.mutate(t.id),
                },
          ];

          if (t.isActive && !t.probePaused) {
            actions.push({
              label: 'Pausar Probes',
              icon: <Pause size={15} />,
              onClick: () => pauseProbeMutation.mutate(t.id),
            });
          }
          if (t.isActive && t.probePaused) {
            actions.push(
              {
                label: 'Retomar Probes',
                icon: <Play size={15} />,
                onClick: () => resumeProbeMutation.mutate(t.id),
              },
              {
                label: 'Limpar Dados',
                icon: <Trash2 size={15} />,
                onClick: () => { setCleanBefore(''); setConfirmCleanData(t); },
                variant: 'danger',
              },
            );
          }

          actions.push({
              label: 'Excluir',
              icon: <Trash2 size={15} />,
              onClick: () => setConfirmDestroy(t),
              variant: 'danger',
          });
          return <RowActionMenu actions={actions} />;
        },
      }),
      columnHelper.accessor('nome', {
        header: 'Nome',
        cell: (info) => <span className="font-medium">{info.getValue()}</span>,
      }),
      columnHelper.accessor('slug', {
        header: 'Slug',
        cell: (info) => <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{info.getValue()}</span>,
      }),
      columnHelper.accessor('urlProbe', {
        header: 'URL Probe',
        cell: (info) => {
          const url = info.getValue();
          return (
            <div className="flex items-center gap-1.5">
              <span className="max-w-[200px] truncate font-mono text-xs text-gray-500 dark:text-gray-400" title={url}>
                {url}
              </span>
              <button
                onClick={() => navigator.clipboard.writeText(url)}
                className="shrink-0 rounded p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                title="Copiar URL"
              >
                <Copy size={13} />
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 rounded p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                title="Abrir URL"
              >
                <ExternalLink size={13} />
              </a>
            </div>
          );
        },
      }),
      columnHelper.accessor('poolIIS', {
        header: 'Pool IIS',
        cell: (info) => {
          const pool = info.getValue();
          return pool
            ? <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{pool}</span>
            : <span className="text-gray-300 dark:text-gray-600">—</span>;
        },
      }),
      columnHelper.accessor('isActive', {
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <StatusBadge isActive={row.original.isActive} />
            <ProbeBadge probePaused={row.original.probePaused} />
          </div>
        ),
      }),
      columnHelper.display({
        id: 'monitoring',
        header: 'Monitor',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            onClick={() => navigate(`/admin/tenants/${row.original.id}/status`)}
            className="rounded-md p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
            title="Ver Monitoramento"
          >
            <Activity size={16} />
          </button>
        ),
      }),
    ],
    [],
  );

  // ── Tabela ──────────────────────────────────────────────────
  const table = useReactTable({
    data: response?.data ?? [],
    columns,
    pageCount: response ? Math.ceil(response.total / response.limit) : -1,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  // ── VIEW: Formulário ────────────────────────────────────────
  if (viewMode === 'form') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => { setViewMode('list'); setEditingTenant(null); }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="border-b-2 border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-xl font-semibold">{isEditing ? 'Editar Tenant' : 'Novo Tenant'}</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 px-6 py-6">
            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Nome *</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 5h14M3 10h14M3 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>}
                    title="Nome do Tenant"
                    subtitle="Nome de exibição do cliente no painel"
                  />
                  <TooltipBody>
                    <TooltipDetail>Identifica o cliente monitorado em todas as telas do sistema.</TooltipDetail>
                    <TooltipDetail><strong>Limite:</strong> Mínimo de 2 caracteres</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Exemplo:</strong> "Acme Corp", "Globo Educação"</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('nome')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="Nome do tenant"
              />
              {errors.nome && <p className="mt-1 text-xs text-red-500">{errors.nome.message}</p>}
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Slug *</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 7h12M4 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="16" cy="12" r="2" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>}
                    title="Slug"
                    subtitle="Identificador único na URL de acesso"
                  />
                  <TooltipBody>
                    <TooltipDetail>Usado como subdomínio: slug.woli.com.br</TooltipDetail>
                    <TooltipDetail><strong>Formato:</strong> Letras minúsculas, números e hífens (2-50 caracteres)</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Exemplo:</strong> "acme", "globo-educacao"</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('slug')}
                readOnly={isEditing}
                className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 ${
                  isEditing ? 'cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' : ''
                }`}
                placeholder="meu-tenant"
              />
              {errors.slug && <p className="mt-1 text-xs text-red-500">{errors.slug.message}</p>}
              {!isEditing && (
                <p className="mt-1 text-xs text-gray-400">Não pode ser alterado depois da criação.</p>
              )}
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">URL de Probe *</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    title="URL de Probe"
                    subtitle="Endereço verificado pelo worker de monitoramento"
                  />
                  <TooltipBody>
                    <TooltipDetail>O probe faz uma requisição HTTP a cada minuto para verificar disponibilidade.</TooltipDetail>
                    <TooltipDetail><strong>UP:</strong> Qualquer resposta HTTP (servidor respondeu). <strong>DOWN:</strong> Timeout, DNS falha ou connection refused.</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Exemplo:</strong> "https://acme.woli.com.br/health"</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('urlProbe')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="https://tenant.woli.com.br/health"
              />
              {errors.urlProbe && <p className="mt-1 text-xs text-red-500">{errors.urlProbe.message}</p>}
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Pool IIS</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="4" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M3 8h14" stroke="currentColor" strokeWidth="1.5"/><circle cx="6" cy="6" r="0.5" fill="currentColor"/><circle cx="8.5" cy="6" r="0.5" fill="currentColor"/></svg>}
                    title="Pool IIS"
                    subtitle="Application Pool do subdomínio no IIS"
                  />
                  <TooltipBody>
                    <TooltipDetail>Nome do Application Pool configurado no IIS para este subdomínio.</TooltipDetail>
                    <TooltipDetail><strong>Opcional:</strong> campo informativo, sem impacto no monitoramento.</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Exemplo:</strong> "acme.woli.com.br" ou "DefaultAppPool"</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('poolIIS')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="Nome do Application Pool"
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Domínio da página de status</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M3 10h14M10 3c2.5 2.5 2.5 11.5 0 14M10 3c-2.5 2.5-2.5 11.5 0 14" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>}
                    title="Domínio da página de status"
                    subtitle="Host que identifica o tenant na página pública"
                  />
                  <TooltipBody>
                    <TooltipDetail>O redirect /uptime do cliente envia ?host=; quando este campo está preenchido, o tenant é resolvido pelo domínio COMPLETO.</TooltipDetail>
                    <TooltipDetail><strong>Opcional:</strong> em branco, vale a regra padrão (primeiro trecho do host = slug). Preencha quando o domínio do cliente não começa com o slug — ex.: clientes do wolihub.com.br.</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Exemplo:</strong> "cb-aprendamais.wolihub.com.br" (sem https:// e sem barra)</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('dominioStatus')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="cliente.wolihub.com.br"
              />
              {errors.dominioStatus && (
                <p className="mt-1 text-xs text-red-500">{errors.dominioStatus.message}</p>
              )}
            </div>

            {/* ── Contrato / SLA (opcional) ── */}
            <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
              <h3 className="text-sm font-semibold">
                Contrato / SLA <span className="font-normal text-gray-400">(opcional)</span>
              </h3>
              <p className="mt-0.5 text-xs text-gray-400">
                Preencha só se o cliente tem cláusula de SLA/desconto. Em branco = sem cláusula (o relatório fica vazio nessas colunas).
              </p>

              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Início do contrato</label>
                  <input
                    type="date"
                    {...register('contratoInicioEm')}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Meta de SLA (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    {...register('slaAlvo')}
                    placeholder="99.99"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Periodicidade</label>
                  <select
                    {...register('slaPeriodicidade')}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                  >
                    <option value="">—</option>
                    <option value="MENSAL">Mensal</option>
                    <option value="ANUAL">Anual (avaliação futura)</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-medium">Faixas de desconto por disponibilidade</label>
                <p className="mb-2 text-xs text-gray-400">
                  Uptime mínimo (%) → desconto (%). Ex.: 99,98 → 0 · 95 → 2 · 90 → 10.
                </p>
                <div className="space-y-2">
                  {faixaFields.map((f, i) => (
                    <div key={f.id} className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        {...register(`faixasDesconto.${i}.uptimeMin` as const)}
                        placeholder="uptime mín. %"
                        className="w-36 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                      />
                      <ArrowRight size={14} className="flex-shrink-0 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        {...register(`faixasDesconto.${i}.descontoPct` as const)}
                        placeholder="desconto %"
                        className="w-36 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                      />
                      <button
                        type="button"
                        onClick={() => removeFaixa(i)}
                        aria-label="Remover faixa"
                        className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => appendFaixa({ uptimeMin: '', descontoPct: '' })}
                  className="mt-2 flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  <Plus size={14} /> Adicionar faixa
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
              <button
                type="button"
                onClick={() => { setViewMode('list'); setEditingTenant(null); }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── VIEW: Lista ─────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Tenants</h1>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            Gerencie os clientes monitorados e suas URLs de probe
            <button
              onClick={() => setShowHelp(true)}
              className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
              title="Como funciona o monitoramento?"
            >
              <HelpCircle size={16} />
            </button>
          </p>
        </div>
        <button
          onClick={() => { setEditingTenant(null); setViewMode('form'); }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={16} />
          Novo Tenant
        </button>
      </div>

      {/* Painel de filtros */}
      <div className="rounded border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Buscar</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                placeholder="Buscar por nome ou slug..."
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
            <div className="flex items-center gap-4 pt-1">
              {([['all', 'Todos'], ['active', 'Ativos'], ['inactive', 'Inativos']] as const).map(([value, label]) => (
                <label key={value} className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="statusFilter"
                    value={value}
                    checked={statusFilter === value}
                    onChange={() => {
                      setStatusFilter(value as StatusFilter);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    className="h-4 w-4 cursor-pointer accent-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Ações: Pesquisar + Limpar */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            title="Utilize os filtros para buscar e filtrar os tenants"
          >
            <Search size={16} />
            Pesquisar
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 rounded-md bg-gray-400 px-4 py-2 text-sm font-medium text-white hover:bg-gray-500"
          >
            <Eraser size={16} />
            Limpar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {!searched ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <Search size={24} className="mb-1" />
            <p className="text-sm font-medium">Utilize os filtros acima e clique em Pesquisar</p>
          </div>
        ) : isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : response && response.data.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <p className="text-sm font-medium">Nenhum tenant encontrado.</p>
            <p className="text-xs">Adicione novos tenants ou ajuste os filtros de busca.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b-2 border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    return (
                      <th
                        key={header.id}
                        className={`px-4 py-3 ${canSort ? 'cursor-pointer select-none' : ''}`}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            header.column.getIsSorted() === 'asc'
                              ? <ArrowUpNarrowWide size={14} className="text-blue-600 dark:text-blue-400" />
                              : header.column.getIsSorted() === 'desc'
                                ? <ArrowDownWideNarrow size={14} className="text-blue-600 dark:text-blue-400" />
                                : <ArrowUpDown size={14} className="text-gray-300 dark:text-gray-600" />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={`border-b border-gray-100 transition-colors hover:bg-blue-50/30 dark:border-gray-800 dark:hover:bg-blue-900/10 ${Number(row.id) % 2 === 1 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Paginação */}
        {searched && response && response.total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
            <span className="text-gray-500 dark:text-gray-400">
              Mostrando {Math.min(pagination.pageIndex * pagination.pageSize + 1, response.total)} a{' '}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, response.total)} de {response.total} registros
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <span className="text-xs">Resultados por página:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Anterior
                </button>
                <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                  {pagination.pageIndex + 1} / {table.getPageCount()}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmação de desativação */}
      {confirmDeactivate && (
        <ConfirmModal
          title="Desativar Tenant"
          message={`Deseja desativar o tenant "${confirmDeactivate.nome}" (${confirmDeactivate.slug})? Essa ação pode ser revertida posteriormente.`}
          confirmLabel="Desativar"
          onConfirm={() => deactivateMutation.mutate(confirmDeactivate.id)}
          onCancel={() => setConfirmDeactivate(null)}
          isLoading={deactivateMutation.isPending}
        />
      )}

      {/* Modal de confirmação de exclusão */}
      {confirmDestroy && (
        <ConfirmModal
          title="Excluir Tenant"
          message={`Deseja excluir permanentemente o tenant "${confirmDestroy.nome}" (${confirmDestroy.slug})? Esta ação é irreversível e o tenant não poderá ser recuperado.`}
          confirmLabel="Excluir permanentemente"
          onConfirm={() => destroyMutation.mutate(confirmDestroy.id)}
          onCancel={() => setConfirmDestroy(null)}
          isLoading={destroyMutation.isPending}
        />
      )}

      {/* Modal de confirmação de limpeza de dados */}
      {confirmCleanData && (
        <ConfirmModal
          title="Limpar Dados do Tenant"
          message={
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Limpar o histórico de <strong>{confirmCleanData.nome}</strong> ({confirmCleanData.slug}).
                Probes e resumos são <strong>excluídos fisicamente</strong>; incidentes e manutenções
                saem do histórico do tenant. Ação irreversível.
              </p>
              <label className="block text-sm">
                <span className="text-gray-600 dark:text-gray-400">Apagar tudo antes de (opcional):</span>
                <input
                  type="datetime-local"
                  value={cleanBefore}
                  onChange={(e) => setCleanBefore(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                />
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Vazio = apagar <strong>todo</strong> o histórico. Para uma linha 100% limpa, use o
                início do dia (00:00) — o dia do corte é mantido como está.
              </p>
            </div>
          }
          confirmLabel="Limpar dados"
          onConfirm={() =>
            cleanDataMutation.mutate({
              id: confirmCleanData.id,
              before: cleanBefore ? new Date(cleanBefore).toISOString() : undefined,
            })
          }
          onCancel={() => setConfirmCleanData(null)}
          isLoading={cleanDataMutation.isPending}
        />
      )}

      {/* Modal de detalhes do tenant */}
      {detailTenantId && (
        <TenantDetailModal
          tenantId={detailTenantId}
          onClose={() => setDetailTenantId(null)}
        />
      )}

      {/* Modal de ajuda */}
      {showHelp && <TenantHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
